import Logger from '@/services/log/Logger';

export const i18nFormatter = (stringToFormat: string, args: Object): string => {
  let translation = stringToFormat;
  let unusedSlots: RegExpMatchArray | null = null;
  let isArgumentUsed = true;
  const entries = Object.entries(args);
  for (const [key, value,] of entries) {
    isArgumentUsed = !!translation.match(new RegExp('\\{' + key + '\\}', 'gi'))?.length;
    if (!isArgumentUsed) {
      Logger.error(`Argument ${key} nie ma swojego odpowiednika w stringu, ${translation}`);
      return translation;
    }
    translation = translation.replace(new RegExp('\\{' + key + '\\}', 'gi'), value.toString());
  }
  unusedSlots = translation.match(/{[a-zA-Z]+}/g);
  if (unusedSlots?.length) {
    Logger.error(`${unusedSlots.length > 1 ? 'Podane sloty nie mają swoich odpowiedników w argumentach funkcji' : 'Podany slot nie ma swojego odpowiednika w argumentach funkcji'}: ${unusedSlots}, ${translation}`);
    return translation;
  }
  return translation;
};

const SLOT_PATTERN = /\{\{\s*([\w.-]+)\s*\}\}/g;
// A value counts as a link only when the whole of it looks like one — no URL hunting inside a sentence.
const LINK_PATTERN = /^(?:https?:\/\/|www\.)\S+$/i;

const asHref = (value: string): string | undefined => {
  if (!LINK_PATTERN.test(value)) return undefined;
  // Keep the href identical to the label (no `URL` normalisation) so plain-text renderings of the
  // same content don't end up spelling the address out twice.
  const href = /^www\./i.test(value) ? `https://${value}` : value;
  try {
    const {protocol,} = new URL(href);
    return protocol === 'http:' || protocol === 'https:' ? href : undefined;
  } catch {
    return undefined;
  }
};

const fillSlots = (text: string, variables: Record<string, string | number | undefined>): string =>
  text.replace(SLOT_PATTERN, (_match, key: string) => {
    const value = variables[key];
    return value === undefined || value === null ? '' : String(value);
  });

// Fills `{{ key }}` slots in WYSIWYG content stored on the backend. A slot with no matching key is
// dropped: a raw `{{ … }}` in front of a client is worse than a gap. A value that is itself a URL
// becomes a real anchor, unless the slot sits in an attribute (`<a href="{{ link }}">`) or already
// inside a link — nesting anchors there would break the markup instead of formatting it.
//
// Works on the parsed document rather than the raw string so that slot context is known and values
// are escaped by serialisation; sanitising (`v-html-safe`) still happens afterwards.
export const injectHtmlVariables = (html: string, variables: Record<string, string | number | undefined>): string => {
  if (!html.includes('{{')) return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');

  doc.body.querySelectorAll('*').forEach((element) => {
    for (const {name, value,} of Array.from(element.attributes)) {
      if (!value.includes('{{')) continue;
      const filled = fillSlots(value, variables);
      // An empty `href` points at the current page, so an unresolved link slot has to drop the
      // attribute instead — the anchor stays as plain text.
      if (name === 'href' && !filled.trim()) element.removeAttribute(name);
      else element.setAttribute(name, filled);
    }
  });

  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const slotted: Text[] = [];
  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    if (node.data.includes('{{')) slotted.push(node);
  }

  for (const node of slotted) {
    const text = node.data;
    const canLink = !node.parentElement?.closest('a');
    const replacement = doc.createDocumentFragment();
    let restFrom = 0;
    for (const match of text.matchAll(SLOT_PATTERN)) {
      const start = match.index!;
      replacement.append(text.slice(restFrom, start));
      restFrom = start + match[0].length;
      const value = variables[match[1]];
      if (value === undefined || value === null) continue;
      const printed = String(value).trim();
      const href = canLink ? asHref(printed) : undefined;
      if (!href) {
        replacement.append(String(value));
        continue;
      }
      const link = doc.createElement('a');
      link.href = href;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.textContent = printed;
      replacement.append(link);
    }
    replacement.append(text.slice(restFrom));
    node.replaceWith(replacement);
  }

  return doc.body.innerHTML;
};
